import React from "react";
import { pipe } from "fp-ts/lib/function";
import Button from "../../OldFront/Simple/ButtonComponent/Button";
import { CRMTableSheet, TCRMTableColumn } from "../../Simple/CRMTableSheet/CRMTableSheet";
import { CRMStatusPill } from "../../Simple/CRMStatusPill/CRMStatusPill";
import { WeightBold } from "../../WeightBold/WeightBold";
import { TextColor } from "../../BuildingBlocks/TextColor";
import { Background } from "../../BuildingBlocks/Background";
import { SpacingRow } from "../../BuildingBlocks/SpacingRow";
import { CRMSpacing } from "../../../models/CRMSpacing";
import { CRMColors } from "../../../models/CRMColors";
import { listingStatusPillStyle, listingStatusShortLabel } from "./listingStatusPillStyle";
import { trimWithEllipsis } from "../../../../../shared/src/utilsByDomain/string/trimWithEllipsis";
import * as Listing3 from "../../../../../domain/models/Listing3";
import * as ListingSeller1 from "../../../../../domain/models/ListingSeller1";
import * as DisplayAddress from "../../../../../domain/models/DisplayAddress";

type TCRMListingTableProps = {
    listings: Array<Listing3.T>;
    showLoadMore: boolean;
    onLoadMore: () => void;
    onListingClick: (listing: Listing3.T) => void;
    onSellerClick: (listing: Listing3.T) => void;
};

const formatPrice = (price: number | null): string =>
    price === null ? "TBC" : `£${price.toLocaleString("en-GB")}`;

const sellerLabel = (listing: Listing3.T): string => {
    const sellers = listing.sellers ?? [];
    return sellers.length > 0
        ? ListingSeller1.namesToString(sellers)
        : listing.seller_company_name !== ""
            ? listing.seller_company_name
            : "—";
};

const addressLabel = (listing: Listing3.T): string =>
    pipe(DisplayAddress.fromListing3(listing), DisplayAddress.toString);

const renderCount = (value: number): React.ReactNode =>
    value > 0
        ? <WeightBold>{value.toString()}</WeightBold>
        : <TextColor color={CRMColors.NEUTRAL_6}>{value.toString()}</TextColor>;

export const CRMListingTable = (props: TCRMListingTableProps): JSX.Element => {
    const columns: Array<TCRMTableColumn<Listing3.T>> = [
        {
            label: "Address",
            width: "2.5fr",
            render: (listing) => <WeightBold>{trimWithEllipsis(addressLabel(listing), 40)}</WeightBold>,
        },
        {
            label: "Seller(s)",
            width: "2fr",
            render: (listing) =>
                <span onClick={(e) => { e.stopPropagation(); props.onSellerClick(listing); }}>
                    <TextColor color={CRMColors.PRIMARY_4}>
                        {trimWithEllipsis(sellerLabel(listing), 34)}
                    </TextColor>
                </span>,
        },
        {
            label: "Status",
            width: "1.6fr",
            render: (listing) => {
                const style = listingStatusPillStyle(listing.status);
                return (
                    <CRMStatusPill
                        pillColor={style.pillColor}
                        borderColor={style.borderColor}
                        textColor={style.textColor}
                        label={listingStatusShortLabel(listing.status)}
                    />
                );
            },
        },
        {
            label: "Viewings",
            width: "0.8fr",
            render: (listing) => renderCount(listing.viewings_count ?? 0),
        },
        {
            label: "Offers",
            width: "0.8fr",
            render: (listing) => renderCount(listing.offers_count ?? 0),
        },
        {
            label: "Price",
            width: "1.1fr",
            render: (listing) => formatPrice(listing.price),
        },
    ];

    return (
        <CRMTableSheet
            columns={columns}
            sections={[{ rows: props.listings }]}
            getRowKey={(listing) => listing.id}
            onRowClick={props.onListingClick}
            emptyMessage="No listings to show."
            footer={props.showLoadMore
                ? <Background padding={`${CRMSpacing.MEDIUM} 0px`}>
                    <SpacingRow justifyContent="center">
                        <Button
                            label="Load more listings"
                            type="frog"
                            fullWidth={false}
                            onClick={props.onLoadMore}
                        />
                    </SpacingRow>
                </Background>
                : undefined}
        />
    );
};
